import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { StaffBookingFormModal } from './StaffBookingFormModal';
import { getStaffBookingColumns } from './staffBookingColumns';
import { useStaffBookings } from '../../hooks/useStaffBookings';
import { useStaff } from '../../hooks/useStaff';
import type { StaffBookingRequest, StaffBookingResponse } from '../../api/generated';
import {
  BookingListSection,
  DeleteBookingModal,
  formatRecurrenceSummary,
  isRecurringTime,
  joinEntityNames,
  useBookingList
} from '../Bookings/shared';

const TABLE_MIN_WIDTH = 820;
const KEY_PREFIX = 'staff-booking';

export const StaffBookings = () => {
  const { t } = useTranslation();
  const { locationId: routeLocationId } = useParams<{ locationId: string }>();
  const {
    bookings,
    isLoading,
    loadError,
    locationId,
    refresh,
    createBooking,
    updateBooking,
    deleteBooking
  } = useStaffBookings(routeLocationId ?? null);
  const { staff, loadError: staffLoadError, refresh: refreshStaff } = useStaff(
    routeLocationId ?? null
  );

  // Staff bookings may overlap (e.g. a meeting across the lunch booked for all staff), so there is
  // no overlap/409 path — a failure is generic.
  const list = useBookingList<StaffBookingResponse, StaffBookingRequest>({
    keyPrefix: KEY_PREFIX,
    createBooking,
    updateBooking,
    deleteBooking,
    hasConflictError: false
  });

  const staffNameById = useMemo(
    () => new Map(staff.map((member) => [member.id, member.fullName])),
    [staff]
  );

  const columns = useMemo(
    () =>
      getStaffBookingColumns(t, {
        staffNameById,
        onEdit: list.openEdit,
        onDelete: list.requestDelete
      }),
    [t, staffNameById, list.openEdit, list.requestDelete]
  );

  // Who the booking is for, for the delete confirmation: the "all staff" label, or the resolved names.
  const staffLabelFor = (booking: StaffBookingResponse): string =>
    booking.appliesToAllStaff
      ? t('staff-booking-all-staff')
      : joinEntityNames(booking.staffIds, staffNameById);

  // Either the bookings or the staff list failing to load leaves the page unusable, so surface
  // whichever failed and let a single retry refetch both.
  const errorMessage = loadError ?? staffLoadError;
  const handleRetry = () => {
    refresh();
    refreshStaff().catch(() => {
      // staff load errors already surface through staffLoadError
    });
  };

  return (
    <BookingListSection
      keyPrefix={KEY_PREFIX}
      isLoading={isLoading}
      errorMessage={errorMessage}
      locationId={locationId}
      columns={columns}
      bookings={bookings}
      minWidth={TABLE_MIN_WIDTH}
      onAdd={list.openCreate}
      onRetry={handleRetry}
    >
      {list.isFormOpen && (
        <StaffBookingFormModal
          booking={list.editingBooking}
          staff={staff}
          onSave={(payload) => {
            list.handleSave(payload);
          }}
          onClose={list.closeForm}
        />
      )}

      {list.deleteTarget && (
        <DeleteBookingModal
          titleKey="staff-booking-delete-title"
          confirmKey="staff-booking-delete-confirm"
          messageKey="staff-booking-delete-message"
          recurringMessageKey="staff-booking-delete-recurring-message"
          name={staffLabelFor(list.deleteTarget)}
          recurrenceSummary={
            isRecurringTime(list.deleteTarget.time)
              ? formatRecurrenceSummary(t, list.deleteTarget.time)
              : undefined
          }
          onConfirm={() => {
            list.handleConfirmDelete();
          }}
          onClose={list.cancelDelete}
        />
      )}
    </BookingListSection>
  );
};
